import { useEffect, useState } from 'react';

import getYoutubeThumbnail from '../utils/getYoutubeThumbnail';
import resolveIframelyEmbed from '../utils/resolveIframelyEmbed';
import { decodeVideoUrl } from '../utils/decodeHtmlEntities';

// iframe.ly lookups keyed by decoded media URL, shared across mounts so carousel
// re-renders don't refetch. Values are the resolved thumbnail or null.
const embedThumbCache = new Map();

// Only pasted links go to iframe.ly. Uploads carry presigned S3 URLs that must not
// leave our infrastructure, and their signatures change per fetch so they'd never cache.
const IFRAMELY_MEDIA_TYPES = ['EMBED_LINK', 'EXTERNAL_URL'];

const getSyncThumbnail = (mediaUrl, thumbnailUrl) => (
  thumbnailUrl || getYoutubeThumbnail(decodeVideoUrl(mediaUrl)) || null
);

/**
 * Resolves a thumbnail for a video: the API-provided thumbnail first, then a
 * derived YouTube thumbnail, then an iframe.ly lookup for other hosts (Vimeo,
 * Drive, …) when the media is a pasted link (`mediaType` EMBED_LINK or
 * EXTERNAL_URL). Returns null when nothing resolves.
 */
const useVideoThumbnail = (mediaUrl, thumbnailUrl, mediaType) => {
  const syncThumb = getSyncThumbnail(mediaUrl, thumbnailUrl);
  const decodedUrl = mediaUrl && IFRAMELY_MEDIA_TYPES.includes(mediaType)
    ? decodeVideoUrl(mediaUrl)
    : null;
  // Tagged with its URL so a stale lookup never shows after the URL changes.
  const [embedThumb, setEmbedThumb] = useState({ url: null, href: null });

  useEffect(() => {
    // Cached lookups are read straight from the Map during render.
    if (syncThumb || !decodedUrl || embedThumbCache.has(decodedUrl)) return undefined;
    let cancelled = false;
    resolveIframelyEmbed(decodedUrl)
      .then(({ thumbnailUrl: href }) => href || null)
      .catch(() => null)
      .then((href) => {
        embedThumbCache.set(decodedUrl, href);
        if (!cancelled) setEmbedThumb({ url: decodedUrl, href });
      });
    return () => { cancelled = true; };
  }, [syncThumb, decodedUrl]);

  if (syncThumb) return syncThumb;
  if (decodedUrl && embedThumbCache.has(decodedUrl)) return embedThumbCache.get(decodedUrl);
  return embedThumb.url === decodedUrl ? embedThumb.href : null;
};

export default useVideoThumbnail;
